"use client";

import React, { useState, useEffect } from "react";
import { 
  Stethoscope, 
  Users, 
  Calendar, 
  TrendingUp, 
  RotateCcw, 
  Search, 
  Filter, 
  CheckCircle2,
  Sparkles,
  Activity
} from "lucide-react";
import DentalKanban from "./DentalKanban";
import DentalStageModal from "./DentalStageModal";
import DentalDetailModal from "./DentalDetailModal";
import { DentalPatientLead } from "@/lib/demos/dentalDb";

interface DentalCrmDashboardProps {
  currency?: "INR" | "USD";
}

export default function DentalCrmDashboard({ currency = "INR" }: DentalCrmDashboardProps) {
  const [patients, setPatients] = useState<DentalPatientLead[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [treatmentFilter, setTreatmentFilter] = useState("ALL");

  // Modals
  const [selectedPatient, setSelectedPatient] = useState<DentalPatientLead | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const [pendingStageChange, setPendingStageChange] = useState<{
    patient: DentalPatientLead;
    targetStage: DentalPatientLead["stage"];
  } | null>(null);

  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 4000);
  };

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/demos/dental/patients");
      const data = await res.json();
      if (data.success) {
        setPatients(data.patients);
        setStats(data.stats);
      }
    } catch (err) {
      console.error("Failed to load dental patients:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  const handleSelectPatient = (p: DentalPatientLead) => {
    setSelectedPatient(p);
    setIsDetailOpen(true);
  };

  const handleUpdateStage = async (id: number, newStage: DentalPatientLead["stage"]) => {
    const target = patients.find((p) => p.id === id);
    if (!target || target.stage === newStage) return;

    // Modals for stages requiring operational inputs
    if (
      newStage === "Preliminary 3D Scan Scheduled" ||
      newStage === "Treatment Plan Presented" ||
      newStage === "Procedure Completed (Won)" ||
      newStage === "6-Month Recall Scheduled"
    ) {
      setPendingStageChange({
        patient: target,
        targetStage: newStage,
      });
      return;
    }

    // Direct transition
    try {
      const res = await fetch(`/api/demos/dental/patients/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stage: newStage }),
      });
      const data = await res.json();
      if (data.success) {
        setPatients((prev) =>
          prev.map((p) => (p.id === id ? data.patient : p))
        );
        showToast(data.automationMessage || `Patient moved to ${newStage}`);
      }
    } catch (err) {
      console.error("Failed to update stage:", err);
    }
  };

  const handleConfirmStageModal = async (opts: any) => {
    if (!pendingStageChange) return;
    const { patient } = pendingStageChange;

    try {
      const res = await fetch(`/api/demos/dental/patients/${patient.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(opts),
      });
      const data = await res.json();
      if (data.success) {
        setPatients((prev) =>
          prev.map((p) => (p.id === patient.id ? data.patient : p))
        );
        showToast(data.automationMessage || `Patient moved to ${opts.stage}`);
      }
    } catch (err) {
      console.error("Failed to complete stage transition:", err);
    } finally {
      setPendingStageChange(null);
    }
  };

  const handleDeletePatient = async (id: number) => {
    try {
      const res = await fetch(`/api/demos/dental/patients/${id}`, {
        method: "DELETE",
      });
      if (res.ok) {
        setPatients((prev) => prev.filter((p) => p.id !== id));
        showToast("Patient record archived");
      }
    } catch (err) {
      console.error("Failed to delete patient:", err);
    }
  };

  const filteredPatients = patients.filter((p) => {
    const matchesSearch =
      p.patient_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.phone && p.phone.includes(searchQuery)) ||
      p.treatment_interest.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.preferred_doctor.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesTreatment =
      treatmentFilter === "ALL" || p.treatment_interest.includes(treatmentFilter);

    return matchesSearch && matchesTreatment;
  });

  const sym = currency === "USD" ? "$" : "₹";

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-[1600px] mx-auto min-h-screen">
      {/* Toast */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 bg-slate-900 border border-teal-500/40 text-white px-4 py-3 rounded-xl shadow-2xl flex items-center gap-3 animate-in fade-in slide-in-from-bottom-3 duration-200">
          <CheckCircle2 className="w-5 h-5 text-teal-400 shrink-0" />
          <span className="text-xs font-semibold">{toastMessage}</span>
        </div>
      )}

      {/* Header Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-slate-800">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2.5 py-0.5 rounded-full bg-teal-500/10 border border-teal-500/30 text-[11px] font-bold text-teal-400 uppercase tracking-wider">
              Specialized Medical Clinic Demo
            </span>
            <span className="text-xs text-slate-400 font-medium">• Aesthetica Dental</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
            Clinic Floor &amp; Patient EHR Pipeline
          </h1>
          <p className="text-xs sm:text-sm text-slate-400">
            Automated 3D scan passes, treatment plan proposals, operative tracking, and clinical warranty invoices.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchPatients}
            className="p-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-300 border border-slate-800 text-xs font-semibold transition-colors flex items-center gap-1.5"
            title="Refresh clinical pipeline"
          >
            <RotateCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin text-teal-400" : ""}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* KPI Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-slate-900/70 border border-slate-800/90 space-y-2">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-xs font-semibold">Total Patients</span>
            <Users className="w-4 h-4 text-blue-400" />
          </div>
          <div className="text-2xl font-black text-white">
            {stats?.totalPatients ?? patients.length}
          </div>
          <span className="text-[11px] text-slate-400 block">
            Across 6 Clinical Stages
          </span>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900/70 border border-slate-800/90 space-y-2">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-xs font-semibold">3D Scans Scheduled</span>
            <Calendar className="w-4 h-4 text-teal-400" />
          </div>
          <div className="text-2xl font-black text-teal-400">
            {stats?.scansScheduled ?? 2} Patients
          </div>
          <span className="text-[11px] text-teal-300 block">
            iTero Lumina Imaging Bays
          </span>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900/70 border border-slate-800/90 space-y-2">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-xs font-semibold">Active In-Chair</span>
            <Stethoscope className="w-4 h-4 text-amber-400" />
          </div>
          <div className="text-2xl font-black text-amber-400">
            {stats?.activeInChair ?? 1} Surgical
          </div>
          <span className="text-[11px] text-slate-400 block">
            Operatory Utilization: 88%
          </span>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900/70 border border-slate-800/90 space-y-2">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-xs font-semibold">Clinical Pipeline Value</span>
            <TrendingUp className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="text-2xl font-black text-emerald-400">
            {sym}{(stats?.pipelineRevenue ?? 1165000).toLocaleString()}
          </div>
          <span className="text-[11px] text-slate-400 block">
            Presented &amp; Active Cases
          </span>
        </div>
      </div>

      {/* Search & Treatment Filter */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-slate-900/40 p-3 rounded-2xl border border-slate-800/80">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-3 pointer-events-none" />
          <input
            type="text"
            placeholder="Search patient, doctor, treatment..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-9 pr-4 py-2 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-teal-500 transition-colors"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Filter className="w-3.5 h-3.5 text-slate-400 shrink-0 hidden sm:block" />
          <select
            value={treatmentFilter}
            onChange={(e) => setTreatmentFilter(e.target.value)}
            className="w-full sm:w-auto bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-300 focus:outline-none focus:border-teal-500 transition-colors"
          >
            <option value="ALL">All Clinical Specialties</option>
            <option value="Veneer">Porcelain Veneers</option>
            <option value="Aligner">Clear Aligners</option>
            <option value="Implant">Dental Implants</option>
            <option value="Perio">Laser Periodontics</option>
            <option value="Whitening">Laser Whitening</option>
          </select>
        </div>
      </div>

      {/* Kanban Board */}
      <DentalKanban
        patients={filteredPatients}
        onSelectPatient={handleSelectPatient}
        onUpdateStage={handleUpdateStage}
        currency={currency}
      />

      {/* Stage Transition Modal */}
      {pendingStageChange && (
        <DentalStageModal
          patientLead={pendingStageChange.patient}
          targetStage={pendingStageChange.targetStage}
          isOpen={true}
          onClose={() => setPendingStageChange(null)}
          onConfirm={handleConfirmStageModal}
        />
      )}

      {/* Detail EHR Modal */}
      <DentalDetailModal
        patient={selectedPatient}
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedPatient(null);
        }}
        onDelete={handleDeletePatient}
      />
    </div>
  );
}
